import { Injectable } from "@angular/core";
import { Observable, Subject, of } from "rxjs";
import { map, tap, catchError } from "rxjs/operators";
import { FlowQueryModel } from "src/app/models/flowquerymodel";
import { FlowServiceService } from "./flow-service.service";

export type PermissionActionsMap = {
  [feature: string]: { [action: string]: boolean };
};

@Injectable({
  providedIn: "root",
})
export class PermissionsService {
  private readonly permissionsUpdatedSubject =
    new Subject<PermissionActionsMap>();

  /** Emits whenever PremissionToActions is rewritten from a successful refresh. */
  readonly permissionsUpdated$ = this.permissionsUpdatedSubject.asObservable();

  constructor(private flowService: FlowServiceService) {}

  /** Resolve the permission-table role name from a logged-in user model. */
  resolveEffectiveRole(userData: any): string {
    if (!userData) {
      return "";
    }
    // Prefer camelCase; fall back to PascalCase from older/partial payloads.
    const userRole = userData.userRole ?? userData.UserRole ?? "";
    const userType = userData.userType ?? userData.UserType ?? "";

    // userRole is often null for plan-based creators; userType holds "Content Creator".
    // Only treat explicit "User" (or empty/null role) as a signal to use userType.
    let effectiveRole =
      !userRole || userRole === "User" ? userType || userRole : userRole;

    if (effectiveRole === "Content Manager") {
      effectiveRole = "Content Creator";
    } else if (effectiveRole === "Business Creator") {
      effectiveRole = "Business";
    }

    return effectiveRole || "";
  }

  /**
   * Fetch permissions for the user's role, rebuild PremissionToActions from all
   * returned groups, persist to localStorage, and notify subscribers.
   */
  refresh(userData?: any): Observable<PermissionActionsMap> {
    const user =
      userData ??
      (() => {
        try {
          return JSON.parse(localStorage.getItem("LoggedInUser") || "null");
        } catch {
          return null;
        }
      })();

    const userEmail =
      user?.partitionKey ||
      user?.emailAddress ||
      user?.PartitionKey ||
      user?.EmailAddress;
    const effectiveRole = this.resolveEffectiveRole(user);

    if (!userEmail || !effectiveRole) {
      return of({});
    }

    const flowQueryModel = new FlowQueryModel();
    flowQueryModel.userEmail = userEmail;
    flowQueryModel.searchString = effectiveRole;

    return this.flowService.getUserPermissionsDetailsByRole(flowQueryModel).pipe(
      map((data: any[]) => {
        const permissionsList = (Array.isArray(data) ? data : []).filter(
          (item) => item?.permissions != null
        );
        localStorage.setItem("Permissions", JSON.stringify(permissionsList));

        const allPermissions = permissionsList.reduce(
          (acc: any[], group: any) =>
            acc.concat(Array.isArray(group.permissions) ? group.permissions : []),
          []
        );
        return this.buildAndStoreMap(allPermissions);
      }),
      tap((mapResult) => this.permissionsUpdatedSubject.next(mapResult)),
      catchError((error) => {
        console.error("Error loading permissions", error);
        // Clear stale flags so a failed refresh (e.g. old 403) does not leave creator chrome on/off incorrectly.
        const empty = this.buildAndStoreMap([]);
        this.permissionsUpdatedSubject.next(empty);
        return of(empty);
      })
    );
  }

  /** Build capability map from permission rows and persist PremissionToActions. */
  buildAndStoreMap(permissions: any[]): PermissionActionsMap {
    const premissionToActions: PermissionActionsMap = {};

    (permissions || []).forEach((p: any) => {
      if (p?.isValid && p.feature && p.permission) {
        if (!premissionToActions[p.feature]) {
          premissionToActions[p.feature] = {};
        }
        premissionToActions[p.feature][p.permission] = true;
      }
    });

    localStorage.setItem(
      "PremissionToActions",
      JSON.stringify(premissionToActions)
    );
    return premissionToActions;
  }

  getStoredPermissionMap(): PermissionActionsMap {
    try {
      return JSON.parse(localStorage.getItem("PremissionToActions") || "{}") || {};
    } catch {
      return {};
    }
  }

  /**
   * True when the user may create/edit flows and bundles by role permission.
   * Super Admin always allowed; others need Flow/Bundle → Create/Edit.
   */
  hasFlowBundleCreateEditAccess(userData?: any): boolean {
    const user =
      userData ??
      (() => {
        try {
          return JSON.parse(localStorage.getItem("LoggedInUser") || "null");
        } catch {
          return null;
        }
      })();

    const effectiveRole = this.resolveEffectiveRole(user);
    if (effectiveRole === "Super Admin") {
      return true;
    }

    const map = this.getStoredPermissionMap();
    return !!map["Flow/Bundle"]?.["Create/Edit"];
  }
}
